import React from 'react';

import classNames from 'classnames';
import Cookies from 'universal-cookie';
import PropTypes from 'prop-types';
import { useConfigStore, useAppStore } from '../../../stores';
import { useFavoriteButtons } from './FavoriteButtonsContext';

import { toIsoString } from '../../../utils/common';
import { patchResidentInDrupal } from '../../../utils/DrupalEntityTypes/Resident/ResidentHelpers';
import { combineAndFormatYtSessionsForDrupal } from '../PersonalizationHelpers';

import './Favorite.scss';

const cookies = new Cookies();

const FavoriteButton = ( {
  type,
  uuid,
  hasShadow,
  className,
  floorPlanUuid,
} ) => {
  const crmConfig = useConfigStore((state) => state.crmConfig);
  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const { isFavorited, toggleFavorite } = useFavoriteButtons();

  const { machineName } = crmConfig;
  const favorited = isFavorited( type, uuid );

  const patchResidentInCRM = async () => {
    const residentCookieData = cookies.get( 'resident' );

    // Update end time for current session incase the comm admin doesn't
    // complete the Personaliation process.
    const residentCookie = cookies.get( 'resident' );
    const now = new Date();
    const iso = toIsoString( now );
    residentCookie.yourtour_active_session.end_value = iso;

    const attributesData = await combineAndFormatYtSessionsForDrupal( residentCookie );

    let favoriteRelationships = [];
    if ( type === 'virtual_tour' || type === 'video' || type === 'photo_gallery' ) {
      const cookie = cookies.get( 'media_favorites' );
      const mediaFavoriteTypes = cookie.favorites;
      // Media favorites all get sent to the same field, so even if we have to
      // send ALL of media favorites, not just the ones of this type.
      Object.keys( mediaFavoriteTypes ).forEach( ( mediaType ) => {
        const favorites = mediaFavoriteTypes[mediaType];
        if ( favorites ) {
          favorites.forEach( ( favorite ) => {
            const favoriteRelationship = {
              type: `node--${mediaType}`,
              id: favorite,
            };
            favoriteRelationships.push( favoriteRelationship );
          } );
        }
      } );
      if ( favoriteRelationships.length === 0 ) {
        favoriteRelationships = null;
      }
    } else if ( type === 'residence' ) {
      const cookie = cookies.get( 'residence_favorites' );
      const favorites = cookie.favorites[type];
      if ( favorites ) {
        favorites.forEach( ( favorite ) => {
          const favoriteRelationship = {
            type: `node--${type}`,
            id: favorite,
          };
          favoriteRelationships.push( favoriteRelationship );
        } );
      } else {
        favoriteRelationships = null;
      }
    } else {
      const cookie = cookies.get( 'favorites' );
      const favorites = cookie.favorites[type];
      if ( favorites ) {
        favorites.forEach( ( favorite ) => {
          const favoriteRelationship = {
            type: `node--${type}`,
            id: favorite,
          };
          favoriteRelationships.push( favoriteRelationship );
        } );
      } else {
        favoriteRelationships = null;
      }
    }

    let relationshipsData = {};
    switch ( type ) {
      case 'floor_plan':
        relationshipsData = {
          field_favorite_floor_plans: {
            data: favoriteRelationships,
          },
        };
        break;
      case 'place':
        relationshipsData = {
          field_favorite_amenities: {
            data: favoriteRelationships,
          },
        };
        break;
      case 'residence': {
        relationshipsData = {
          field_favorite_residences: {
            data: favoriteRelationships,
          },
        };

        // Also include floor plan favorites when residence is favorited
        if ( floorPlanUuid ) {
          const fpCookie = cookies.get( 'favorites' );
          const fpFavorites = fpCookie?.favorites?.floor_plan;
          let fpFavoriteRelationships = [];

          if ( fpFavorites ) {
            fpFavorites.forEach( ( favorite ) => {
              const favoriteRelationship = {
                type: 'node--floor_plan',
                id: favorite,
              };
              fpFavoriteRelationships.push( favoriteRelationship );
            } );
          } else {
            fpFavoriteRelationships = null;
          }

          relationshipsData.field_favorite_floor_plans = {
            data: fpFavoriteRelationships,
          };
        }
        break;
      }
      case 'virtual_tour':
      case 'video':
      case 'photo_gallery':
        relationshipsData = {
          field_favorite_media: {
            data: favoriteRelationships,
          },
        };
        break;
      default:
    }

    await patchResidentInDrupal(
      residentCookieData.drupal_uuid,
      attributesData,
      null,
      relationshipsData,
    )
      .catch( ( error ) => {
        // eslint-disable-next-line no-console
        console.log( error.message );
      } );
  };

  const updateSessionCookie = () => {
    const residentCookie = cookies.get( 'resident' );
    if ( residentCookie ) {
      const now = new Date();
      const iso = toIsoString( now );
      residentCookie.yourtour_active_session.end_value = iso;

      cookies.remove( 'resident' );
      cookies.set( 'resident', residentCookie, {
        path: '/',
        maxAge: 14400,
        domain: window.location.hostname,
      } );
    }
  };

  const buttonClick = () => {
    // Toggle favorite state in context (this will update all instances)
    // Pass floorPlanUuid for residence types to enable dual favoriting
    toggleFavorite( type, uuid, type === 'residence' ? floorPlanUuid : null );

    // Update session cookie
    updateSessionCookie();

    // Send data to Drupal.
    if ( machineName === 'drupal' ) {
      patchResidentInCRM();
    }
  };

  return personalizationActive && (
    <div className={classNames(className)}>
      <button
        className={classNames( 'favoriteBtn', {
          'favoriteBtn--active ': favorited,
          'favoriteBtn--shadow ': hasShadow,
        } )}
        onClick={buttonClick}
        type="button"
      >
        <i className="material-icons not-liked">favorite_border</i>
        <i className="material-icons is-liked">favorite</i>
        <span className="favoriteBtn-overlay" />
      </button>
    </div>
  );
};

FavoriteButton.propTypes = {
  type: PropTypes.string.isRequired,
  uuid: PropTypes.string.isRequired,
  hasShadow: PropTypes.bool,
  className: PropTypes.string,
  floorPlanUuid: PropTypes.string,
};

FavoriteButton.defaultProps = {
  hasShadow: false,
  className: '',
  floorPlanUuid: null,
};

export default FavoriteButton;
